"use client";

import { useCallback, useMemo, useState } from "react";
import type { MouseEvent as ReactMouseEvent, FocusEvent as ReactFocusEvent } from "react";

export type CommunityFeature = {
  name: string;
  display_name: string;
  count: number;
  path: string;
};

export type CommunityMapData = {
  viewbox: [number, number, number, number];
  features: CommunityFeature[];
  buckets: { label: string; count: number }[];
  unmatched: { name: string; count: number }[];
};

type Props = {
  data: CommunityMapData;
};

// Discrete bins keep the legend honest given the long-tail distribution
// (Tuscany 40, then a thick band of 10–25, then a long tail of 1–9).
const BINS = [
  { min: 0, max: 0, color: "var(--map-eng-1)", label: "0" },
  { min: 1, max: 4, color: "var(--map-eng-2)", label: "1–4" },
  { min: 5, max: 9, color: "var(--map-eng-3)", label: "5–9" },
  { min: 10, max: 19, color: "var(--map-eng-4)", label: "10–19" },
  { min: 20, max: 29, color: "var(--map-eng-5)", label: "20–29" },
  { min: 30, max: Infinity, color: "var(--map-eng-6)", label: "30+" },
];

function colourFor(count: number): string {
  for (const b of BINS) {
    if (count >= b.min && count <= b.max) return b.color;
  }
  return BINS[0].color;
}

export function EngagementMap({ data }: Props) {
  const [hover, setHover] = useState<CommunityFeature | null>(null);

  const sortedTop = useMemo(
    () =>
      [...data.features]
        .filter((f) => f.count > 0)
        .sort((a, b) => b.count - a.count)
        .slice(0, 10),
    [data.features],
  );

  const totalOnMap = useMemo(
    () => data.features.reduce((s, f) => s + f.count, 0),
    [data.features],
  );

  const featuresByName = useMemo(() => {
    const m = new Map<string, CommunityFeature>();
    for (const f of data.features) m.set(f.name, f);
    return m;
  }, [data.features]);

  const handlePointerOver = useCallback(
    (e: ReactMouseEvent<SVGGElement>) => {
      const t = e.target as SVGElement;
      const name = t.getAttribute?.("data-name");
      if (!name) return;
      const f = featuresByName.get(name);
      if (f) setHover(f);
    },
    [featuresByName],
  );
  const handlePointerOut = useCallback((e: ReactMouseEvent<SVGGElement>) => {
    const related = e.relatedTarget as Element | null;
    if (related && (related as Element).getAttribute?.("data-name")) return;
    setHover(null);
  }, []);
  const handleFocus = useCallback(
    (e: ReactFocusEvent<SVGGElement>) => {
      const name = (e.target as SVGElement).getAttribute?.("data-name");
      if (!name) return;
      const f = featuresByName.get(name);
      if (f) setHover(f);
    },
    [featuresByName],
  );
  const handleBlur = useCallback(() => setHover(null), []);

  // Memoize the static path layer so hover state changes don't re-render
  // all ~313 paths. The active highlight is rendered as a single overlay
  // path below, so this layer is invariant in `hover`.
  const pathLayer = useMemo(
    () => (
      <g
        onMouseOver={handlePointerOver}
        onMouseOut={handlePointerOut}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        {data.features.map((f) => (
          <path
            key={f.name}
            data-name={f.name}
            d={f.path}
            fill={colourFor(f.count)}
            stroke="var(--bg)"
            strokeWidth={0.4}
            style={{ cursor: "pointer" }}
            tabIndex={0}
          >
            <title>{`${f.display_name}: ${f.count} participant${f.count === 1 ? "" : "s"}`}</title>
          </path>
        ))}
      </g>
    ),
    [data.features, handlePointerOver, handlePointerOut, handleFocus, handleBlur],
  );

  return (
    <div className="grid grid-cols-12 gap-4">
      <div className="col-span-12 lg:col-span-8">
        <div className="relative aspect-square w-full bg-bg border border-rule rounded-md overflow-hidden">
          <svg
            viewBox={data.viewbox.join(" ")}
            className="w-full h-full"
            role="img"
            aria-label="Choropleth of Calgary community districts coloured by participant count"
          >
            {pathLayer}
            {hover ? (
              <path
                d={hover.path}
                fill="none"
                stroke="var(--signal)"
                strokeWidth={1.5}
                vectorEffect="non-scaling-stroke"
                pointerEvents="none"
                aria-hidden="true"
              />
            ) : null}
          </svg>

          {/* Hover readout */}
          <div className="absolute top-3 left-3 bg-surface/90 backdrop-blur-sm border border-rule rounded px-3 py-2 text-sm pointer-events-none min-w-[180px]">
            {hover ? (
              <>
                <div className="cs-mono text-ink-3 text-xs">community</div>
                <div className="font-medium leading-tight">{hover.display_name}</div>
                <div className="cs-num text-lg mt-0.5">
                  {hover.count}{" "}
                  <span className="cs-mono text-xs text-ink-3">
                    participant{hover.count === 1 ? "" : "s"}
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className="cs-mono text-ink-3 text-xs">hover a district</div>
                <div className="cs-num text-lg mt-0.5">{totalOnMap.toLocaleString("en-CA")}</div>
                <div className="cs-mono text-xs text-ink-3">total mapped</div>
              </>
            )}
          </div>

          {/* Legend */}
          <div className="absolute bottom-3 left-3 bg-surface/90 backdrop-blur-sm border border-rule rounded px-3 py-2 text-xs">
            <div className="cs-mono text-ink-3 mb-1.5">participants</div>
            <div className="flex items-center gap-1">
              {BINS.map((b) => (
                <div key={b.label} className="flex flex-col items-center">
                  <div
                    className="w-7 h-3"
                    style={{ background: b.color, border: "1px solid var(--bg)" }}
                  />
                  <div className="cs-mono text-[11px] mt-0.5 text-ink-3">{b.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="col-span-12 lg:col-span-4 space-y-5 text-sm">
        <div>
          <div className="cs-label mb-2">Top 10 communities</div>
          <ol className="list-none p-0 space-y-1">
            {sortedTop.map((f, i) => (
              <li
                key={f.name}
                className="flex items-baseline gap-3 py-0.5 cs-rule first:border-t-0 first:pt-0 pt-1.5"
                onMouseEnter={() => setHover(f)}
                onMouseLeave={() => setHover(null)}
              >
                <span className="cs-num text-ink-3 w-5 flex-none text-right">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="flex-1 leading-tight">{f.display_name}</span>
                <span className="cs-num">{f.count}</span>
              </li>
            ))}
          </ol>
        </div>

        {data.buckets.length > 0 && (
          <div>
            <div className="cs-label mb-2">Not mapped — bucketed responses</div>
            <ul className="list-none p-0 space-y-0.5 text-ink-2">
              {data.buckets.map((b) => (
                <li
                  key={b.label}
                  className="flex items-baseline justify-between gap-3"
                >
                  <span className="leading-tight">{b.label}</span>
                  <span className="cs-num text-ink-3">{b.count}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {data.unmatched.length > 0 && (
          <div>
            <div className="cs-label mb-2">
              Not mapped — no matching boundary
            </div>
            <ul className="list-none p-0 space-y-0.5 text-ink-2">
              {data.unmatched.map((u) => (
                <li
                  key={u.name}
                  className="flex items-baseline justify-between gap-3"
                >
                  <span className="leading-tight">{u.name}</span>
                  <span className="cs-num text-ink-3">{u.count}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-ink-3 mt-2 leading-snug">
              These names appear in the report but don&rsquo;t resolve to a
              City community-district boundary (sub-communities, informal area
              names, or regional areas outside the 313-district file).
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
